import { afterNextRender, ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { type FormControl, ReactiveFormsModule } from '@angular/forms';

import { BaseFormField, FieldError } from '@forthbridge/forms';

import { TranslateModule } from '@ngx-translate/core';

import { type WeightInputConfig, type WeightInputState } from './weight-input.types';

@Component({
  selector: 'app-weight-input',
  templateUrl: './weight-input.html',
  styleUrl: './weight-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslateModule, FieldError],
})
export class WeightInput extends BaseFormField<number | null, FormControl<number | null>> {
  readonly config = input<WeightInputConfig>({});
  readonly disabled = input<boolean>(false);

  protected readonly currentState = signal<WeightInputState>('idle');

  protected readonly inputLabel = computed(() => this.config().label || this.label);

  constructor() {
    super();
    afterNextRender(() => this.updateState());
  }

  override onFocus(): void {
    super.onFocus();
    this.updateState();
  }

  override onBlur(): void {
    super.onBlur();
    this.updateState();
  }

  // Uses input event instead of keypress because input[type="number"] returns null for
  // selectionStart/selectionEnd, which blocked typing over selected text. Trims after entry
  // and syncs the form control with the corrected value.
  protected enforceMaxLength(event: Event, maxLength: number): void {
    const input = event.target as HTMLInputElement;
    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
      this.control?.setValue(input.value ? Number(input.value) : null);
    }
  }

  override onChange(): void {
    super.onChange();
    this.updateState();
  }

  override onInput(): void {
    super.onInput();
    this.updateState();
  }

  private updateState(): void {
    if (this.isDisabledState) {
      this.currentState.set('disabled');
    } else if (this.invalid) {
      this.currentState.set('error');
    } else if (this.isFocused || this.control?.value != null) {
      this.currentState.set('filled');
    } else {
      this.currentState.set('idle');
    }
  }

  protected readonly containerClass = computed(() => {
    return 'flex items-center justify-center';
  });

  protected readonly containerStyle = computed(() => {
    const state = this.currentState();
    return {
      gap: 'var(--measurementinput-gap)',
      paddingLeft: 'var(--measurementinput-padding-x)',
      paddingRight: 'var(--measurementinput-padding-x)',
      paddingTop: 'var(--measurementinput-padding-y)',
      paddingBottom: 'var(--measurementinput-padding-y)',
      borderRadius: 'var(--measurementinput-border-radius)',
      fontFamily: 'var(--measurementinput-font-family)',
      background: `var(--measurementinput-${state}-background)`,
      borderWidth: `var(--measurementinput-${state}-border-width)`,
      borderColor: `var(--measurementinput-${state}-border-color)`,
      borderStyle: 'solid',
    };
  });

  protected readonly placeholderColor = computed(() => {
    const state = this.currentState();
    return `var(--measurementinput-${state}-placeholder-color)`;
  });
}
